/**
 * Integrytas Group — CDS (Content Dataset Service): Dataset Builder / Registry.
 * Page: CDS.DATASET.LIST
 *
 * The technical author's home for saved datasets. Generalises the old dmo Report
 * Writer: pick a source (a DSE flat-view entity, a SQL view, or a read-only RPC),
 * whitelist its columns (reflected via cds_source_columns), declare filter params,
 * and save a reusable cds_dataset — which a content editor then drops onto a CMS
 * page as a 'dataset' object. A live preview runs cds_run so the author sees exactly
 * what a viewer will get (RLS applies as the caller).
 *
 * Column/param child rows have composite PKs and are written directly (the same
 * convention CMS uses for its _tl companions); the cds_dataset header routes through
 * the Common Routine (§2).
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDb } from '../../lib/api_client';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import { hasAccess, getForensicActorId  } from '../../types/auth';
import type { AuthSession } from '../../types/auth';
import {
  PersonalizationProvider, PersonalizationToggle, PersonalizedGrid,
  type GridColumnDef,
} from '../../components/personalization';
import { Modal } from '../../components/common/Modal';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { FilteredModalSingleSelect } from '../../components/common/FilteredModalSingleSelect';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { PageHeader } from '../../components/common/PageHeader';
import { ContentCard } from '../../components/common/ContentCard';
import { Plus, RefreshCw, Search } from 'lucide-react';
import type {
  CdsDatasetRow, CdsSourceKind, CdsScopeMode, CdsOperator, CdsParamSource, CdsRunResult, CdsRunColumn,
} from '../../types/cds';

const PAGE_CODE = 'CDS.DATASET.LIST';

interface SourceCol { key: string; data_type: string }
interface DraftCol { column_name: string; data_type: string; include: boolean; format_hint: string; is_filterable: boolean; is_sortable: boolean }
interface DraftParam { param_code: string; bind_column: string; operator: CdsOperator; source: CdsParamSource; is_required: boolean }

const SOURCE_KINDS: CdsSourceKind[] = ['DDV', 'VIEW', 'RPC'];
const SCOPE_MODES: CdsScopeMode[]   = ['COMPANY_ONLY', 'SHARED', 'CROSS_PARTY'];
const OPERATORS: CdsOperator[]      = ['eq', 'in', 'gte', 'lte', 'gt', 'lt', 'like'];
const PARAM_SOURCES: CdsParamSource[] = ['ELEMENT_INPUT', 'STATIC', 'CONTEXT_COMPANY', 'CONTEXT_USER'];

export function CdsDatasetListPage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <CdsDatasetListInner session={session} />
    </PersonalizationProvider>
  );
}

function CdsDatasetListInner({ session }: { session: AuthSession }) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const [rows, setRows] = useState<CdsDatasetRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [fKind, setFKind] = useState<string[]>([]);
  const [fScope, setFScope] = useState<string[]>([]);
  // §17.B12: Active facet defaults to Active-only on first load.
  const [fActive, setFActive] = useState<string[]>(['Y']);
  const [editing, setEditing] = useState<CdsDatasetRow | null | undefined>(undefined); // undefined=closed, null=new
  const [delTarget, setDelTarget] = useState<CdsDatasetRow | null>(null);

  const load = useCallback(async () => {
    setBusy(true); setError(null);
    const { data, error: e } = await apiDb.from('cds_dataset')
      .select('dataset_id, internal_code, name, source_kind, source_ref, default_order, max_rows, can_write, public_anon, scope_mode, is_active')
      .order('internal_code');
    if (e) setError(e.message);
    else setRows((data ?? []) as CdsDatasetRow[]);
    setBusy(false);
  }, []);
  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => rows.filter(r => {
    if (fKind.length && !fKind.includes(r.source_kind)) return false;
    if (fScope.length && !fScope.includes(r.scope_mode)) return false;
    if (fActive.length && !fActive.includes(r.is_active)) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      if (!r.internal_code.toLowerCase().includes(q) && !r.name.toLowerCase().includes(q) && !r.source_ref.toLowerCase().includes(q)) return false;
    }
    return true;
  }), [rows, fKind, fScope, fActive, search]);

  const filtersActive = !!(search || fKind.length || fScope.length || fActive.length);

  // TODO: wire relations — the dataset's column/param child collections are loaded
  // only inside the edit modal, not on the list; surfacing them here would require a
  // new query (out of scope), so the disclosure renders inert (spacer).
  const detail = useExpandedRows<string>();
  const buildRelatedGroups = (_code: string): RelatedChildGroup[] => [];
  const hasRelated = (code: string) => buildRelatedGroups(code).some(g => g.items.length > 0);

  const COLS: GridColumnDef[] = [
    { key: 'internal_code', header: t('cds.ds.col.code', 'Code'), defaultWidth: 200 },
    { key: 'name',          header: t('cds.ds.col.name', 'Name'), defaultWidth: 220 },
    { key: 'source_kind',   header: t('cds.ds.col.source_kind', 'Source'), defaultWidth: 90 },
    { key: 'source_ref',    header: t('cds.ds.col.source_ref', 'Source ref'), defaultWidth: 180 },
    { key: 'scope_mode',    header: t('cds.ds.col.scope', 'Scope'), defaultWidth: 140 },
    { key: 'is_active',     header: t('cds.ds.col.active', 'Active'), defaultWidth: 80 },
  ];

  const doDelete = async () => {
    if (!delTarget) return;
    setError(null);
    const { error: e } = await routedDelete(session, 'cds_dataset', 'dataset_id', delTarget.dataset_id);
    if (e) setError(e.message);
    setDelTarget(null);
    await load();
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('cds.ds.title', 'Datasets')}
          description={t('cds.ds.list.description', 'Reusable saved datasets a content editor can drop onto a CMS page.')}
          actions={
            <>
              <button type="button" onClick={() => void load()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh', 'Refresh')}
              </button>
              <button type="button" onClick={() => setEditing(null)} className="btn-primary flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> {t('cds.ds.new', 'New dataset')}
              </button>
            </>
          }
        />

        {error && (
          <ErrorBanner
            message={t('cds.ds.load_failed', "Couldn't load datasets.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void load()}
            details_label={t('common.details', 'Details')}
          />
        )}

        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('common.search', 'Search…')}
                     className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none" />
            </div>
            <FacetFilter label={t('cds.ds.col.source_kind', 'Source')} options={SOURCE_KINDS.map(k => ({ value: k, label: k }))} selected={fKind} onChange={setFKind} />
            <FacetFilter label={t('cds.ds.col.scope', 'Scope')} options={SCOPE_MODES.map(s => ({ value: s, label: s }))} selected={fScope} onChange={setFScope} />
            <FacetFilter label={t('cds.ds.col.active', 'Active')} options={[{ value: 'Y', label: t('common.boolean.active', 'Active') }, { value: 'N', label: t('common.boolean.inactive', 'Inactive') }]} selected={fActive} onChange={setFActive} />
            <ClearFiltersButton active={filtersActive} onClear={() => { setSearch(''); setFKind([]); setFScope([]); setFActive([]); }} />
            <div className="ml-auto flex items-center gap-2">
              <PersonalizationToggle />
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-auto">
        <PersonalizedGrid componentKey="cds_dataset_list" columns={COLS} sourceTable="cds_dataset">
          {(visibleCols) => (
            <table className="min-w-full text-csm-sm">
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr><th className="w-7 px-1 py-1.5" title={t('cds.ds.related.header', 'Related records')} />{visibleCols.map(c => <th key={c.key} className="px-3 py-1.5 text-left">{c.header}</th>)}<th className="px-3 py-1.5" /></tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.internal_code}>
                  <tr className="border-t border-csm-border-soft hover:bg-csm-surface-alt">
                    <td className="px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton
                        open={detail.isOpen(r.internal_code)}
                        hasChildren={hasRelated(r.internal_code)}
                        onToggle={() => detail.toggle(r.internal_code)}
                        expandLabel={t('cds.ds.related.expand', 'Show related records')}
                        collapseLabel={t('cds.ds.related.collapse', 'Hide related')}
                        tooltip={t('cds.ds.related.tooltip', 'Show related records')}
                      />
                    </td>
                    {visibleCols.map(c => (
                      <td key={c.key} className="px-3 py-1.5">
                        {c.key === 'is_active'
                          ? <YesNoBadge value={r.is_active} yesLabel={t('common.boolean.active', 'Active')} noLabel={t('common.boolean.inactive', 'Inactive')} />
                          : String((r as unknown as Record<string, unknown>)[c.key] ?? '')}
                      </td>
                    ))}
                    <td className="px-3 py-1.5 text-right whitespace-nowrap">
                      {canChange && <button onClick={() => setEditing(r)} className="mr-3 text-csm-xs text-primary hover:underline">{t('common.edit', 'Edit')}</button>}
                      <button onClick={() => setDelTarget(r)} className="text-csm-xs text-csm-danger hover:underline">{t('common.delete', 'Delete')}</button>
                    </td>
                  </tr>
                  {detail.isOpen(r.internal_code) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-t border-csm-border-soft bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r.internal_code)} session={session} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {!busy && filtered.length === 0 && <tr><td colSpan={visibleCols.length + 2} className="py-8"><EmptyState variant={filtersActive ? 'no-match' : 'no-data'} title={t('cds.ds.empty', 'No datasets yet.')} action={filtersActive ? undefined : <button onClick={() => setEditing(null)} className="h-7 rounded border border-primary bg-primary px-3 text-csm-sm text-white hover:bg-primary-hover">{t('cds.ds.new', 'New dataset')}</button>} /></td></tr>}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>

      {editing !== undefined && (
        <DatasetEditModal session={session} dataset={editing} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); void load(); }} />
      )}
      {delTarget && (
        <ConfirmModal title={t('cds.ds.delete_title', 'Delete dataset')}
          message={t('cds.ds.delete_msg', 'Delete "{{name}}"? Placed CMS elements will stop rendering.', { name: delTarget.name })}
          confirmLabel={t('common.delete', 'Delete')} onConfirm={() => void doDelete()} onCancel={() => setDelTarget(null)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add / edit modal — header + column whitelist + params + live preview.
// ---------------------------------------------------------------------------

function DatasetEditModal({ session, dataset, onClose, onSaved }: {
  session: AuthSession; dataset: CdsDatasetRow | null; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isNew = dataset === null;
  const [code, setCode]         = useState(dataset?.internal_code ?? '');
  const [name, setName]         = useState(dataset?.name ?? '');
  const [sourceKind, setSourceKind] = useState<CdsSourceKind>(dataset?.source_kind ?? 'DDV');
  const [sourceRef, setSourceRef]   = useState(dataset?.source_ref ?? '');
  const [scopeMode, setScopeMode]   = useState<CdsScopeMode>(dataset?.scope_mode ?? 'COMPANY_ONLY');
  const [defaultOrder, setDefaultOrder] = useState(dataset?.default_order ?? '');
  const [publicAnon, setPublicAnon] = useState((dataset?.public_anon ?? 'N') === 'Y');
  const [canWrite, setCanWrite]     = useState((dataset?.can_write ?? 'N') === 'Y');
  const [cols, setCols]         = useState<DraftCol[]>([]);
  const [params, setParams]     = useState<DraftParam[]>([]);
  const [preview, setPreview]   = useState<{ columns: CdsRunColumn[]; rows: Record<string, unknown>[] } | null>(null);
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [tab, setTab]           = useState('source');

  // Load existing children when editing.
  useEffect(() => {
    if (!dataset) return;
    void (async () => {
      const { data: c } = await apiDb.from('cds_dataset_column')
        .select('column_name, data_type, format_hint, is_filterable, is_sortable, sort_order')
        .eq('dataset_id', dataset.dataset_id).order('sort_order');
      setCols((c ?? []).map((r: Record<string, unknown>) => ({
        column_name: String(r.column_name), data_type: String(r.data_type), include: true,
        format_hint: (r.format_hint as string) ?? '', is_filterable: r.is_filterable === 'Y', is_sortable: r.is_sortable === 'Y',
      })));
      const { data: p } = await apiDb.from('cds_dataset_param')
        .select('param_code, bind_column, operator, source, is_required').eq('dataset_id', dataset.dataset_id).order('sort_order');
      setParams((p ?? []).map((r: Record<string, unknown>) => ({
        param_code: String(r.param_code), bind_column: String(r.bind_column), operator: r.operator as CdsOperator,
        source: r.source as CdsParamSource, is_required: r.is_required === 'Y',
      })));
    })();
  }, [dataset]);

  const loadSourceColumns = async () => {
    setError(null);
    if (sourceKind === 'RPC') { setError(t('cds.ds.rpc_no_reflect', 'RPC sources define their own output; no columns to reflect.')); return; }
    if (!sourceRef.trim()) { setError(t('cds.ds.need_source_ref', 'Enter a source reference first.')); return; }
    const { data, error: e } = await apiDb.rpc<{ ok: boolean; error_code?: string; columns?: SourceCol[] }>('cds_source_columns', { p_source_kind: sourceKind, p_source_ref: sourceRef });
    if (e) { setError(e.message); return; }
    const res = data as { ok: boolean; error_code?: string; columns?: SourceCol[] } | null;
    if (!res?.ok) { setError(res?.error_code ?? 'CDS.REFLECT_FAILED'); return; }
    // Merge: keep existing include/format choices, add newly-discovered columns.
    setCols(prev => {
      const byName = new Map(prev.map(c => [c.column_name, c]));
      return (res.columns ?? []).map((sc, i) => byName.get(sc.key) ?? ({
        column_name: sc.key, data_type: sc.data_type, include: i < 6, format_hint: '', is_filterable: true, is_sortable: true,
      }));
    });
    setTab('columns');
  };

  const includedCols = useMemo(() => cols.filter(c => c.include), [cols]);

  const save = async (): Promise<string | null> => {
    setBusy(true); setError(null);
    try {
      if (!code.trim() || !name.trim() || !sourceRef.trim()) throw new Error(t('cds.ds.required_fields', 'Code, name and source reference are required.'));
      if (sourceKind !== 'RPC' && includedCols.length === 0) throw new Error(t('cds.ds.need_columns', 'Select at least one column to expose.'));
      const header = {
        internal_code: code.trim(), name: name.trim(), source_kind: sourceKind, source_ref: sourceRef.trim(),
        default_order: defaultOrder.trim() || null, scope_mode: scopeMode,
        public_anon: publicAnon ? 'Y' : 'N', can_write: canWrite ? 'Y' : 'N',
      };
      let datasetId = dataset?.dataset_id ?? '';
      if (isNew) {
        const { row_id, error: e } = await routedInsert(session, 'cds_dataset', header);
        if (e) throw new Error(e.message);
        datasetId = String(row_id);
      } else {
        const { error: e } = await routedUpdate(session, 'cds_dataset', 'dataset_id', datasetId, header);
        if (e) throw new Error(e.message);
      }
      // Composite-PK children — rebuilt directly (same convention as CMS _tl companions).
      const actor = getForensicActorId(session);
      await apiDb.from('cds_dataset_column').delete().eq('dataset_id', datasetId);
      if (includedCols.length) {
        await apiDb.from('cds_dataset_column').insert(includedCols.map((c, i) => ({
          dataset_id: datasetId, column_name: c.column_name, data_type: c.data_type, sort_order: (i + 1) * 10,
          is_filterable: c.is_filterable ? 'Y' : 'N', is_sortable: c.is_sortable ? 'Y' : 'N',
          format_hint: c.format_hint.trim() || null, modified_by: actor,
        })));
      }
      await apiDb.from('cds_dataset_param').delete().eq('dataset_id', datasetId);
      const validParams = params.filter(p => p.param_code.trim() && p.bind_column.trim());
      if (validParams.length) {
        await apiDb.from('cds_dataset_param').insert(validParams.map((p, i) => ({
          dataset_id: datasetId, param_code: p.param_code.trim(), bind_column: p.bind_column, operator: p.operator,
          source: p.source, is_required: p.is_required ? 'Y' : 'N', sort_order: (i + 1) * 10, modified_by: actor,
        })));
      }
      return code.trim();
    } catch (e) { setError((e as Error).message); return null; }
    finally { setBusy(false); }
  };

  const runPreview = async () => {
    const savedCode = await save();
    if (!savedCode) return;
    const { data, error: e } = await apiDb.rpc<CdsRunResult>('cds_run', { p_dataset_code: savedCode, p_params: {} });
    if (e) { setError(e.message); return; }
    const res = data as CdsRunResult | null;
    if (!res?.ok) { setError(res?.error_code ?? 'CDS.RUN_FAILED'); return; }
    setPreview({ columns: res.columns ?? [], rows: res.rows ?? [] });
    setTab('preview');
  };

  const setCol = (idx: number, patch: Partial<DraftCol>) => setCols(cs => cs.map((c, i) => i === idx ? { ...c, ...patch } : c));

  return (
    <Modal
      title={isNew ? t('cds.ds.new', 'New dataset') : t('cds.ds.edit', 'Edit dataset')}
      onClose={onClose} width="3xl"
      tabs={[{ id: 'source', label: t('cds.ds.tab.source', 'Source') }, { id: 'columns', label: t('cds.ds.tab.columns', 'Columns') },
            { id: 'params', label: t('cds.ds.tab.params', 'Filters') }, { id: 'preview', label: t('cds.ds.tab.preview', 'Preview') }]}
      activeTab={tab} onTabChange={setTab}
      notice={error ? <div className="border-b border-csm-danger-border bg-csm-danger-soft px-3 py-1 text-csm-xs text-csm-danger">{error}</div> : undefined}
      footer={
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel', 'Cancel')}</button>
          <button onClick={() => void runPreview()} disabled={busy} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-surface-alt disabled:opacity-60">{t('cds.ds.preview_run', 'Save & preview')}</button>
          <button onClick={() => void save().then(c => { if (c) onSaved(); })} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>
        </div>
      }
    >
      {tab === 'source' && (
        <div className="grid grid-cols-2 gap-3 p-1">
          <label className="text-csm-sm"><span className="mb-1 block text-csm-ink-soft">{t('cds.ds.field.code', 'Code')}</span>
            <input value={code} onChange={e => setCode(e.target.value)} disabled={!isNew} className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm disabled:bg-csm-surface-alt" /></label>
          <label className="text-csm-sm"><span className="mb-1 block text-csm-ink-soft">{t('cds.ds.field.name', 'Name')}</span>
            <input value={name} onChange={e => setName(e.target.value)} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm" /></label>
          <div><FilteredModalSingleSelect label={t('cds.ds.col.source_kind', 'Source')} options={SOURCE_KINDS.map(k => ({ internal_code: k, display_text: k }))} selectedCode={sourceKind} onChange={c => { if (c) setSourceKind(c as CdsSourceKind); }} allowClear={false} /></div>
          <label className="text-csm-sm"><span className="mb-1 block text-csm-ink-soft">{sourceKind === 'DDV' ? t('cds.ds.field.entity', 'Entity (table)') : sourceKind === 'VIEW' ? t('cds.ds.field.view', 'View name') : t('cds.ds.field.function', 'Function name')}</span>
            <input value={sourceRef} onChange={e => setSourceRef(e.target.value)} className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm" /></label>
          <div><FilteredModalSingleSelect label={t('cds.ds.col.scope', 'Scope')} options={SCOPE_MODES.map(s => ({ internal_code: s, display_text: s }))} selectedCode={scopeMode} onChange={c => { if (c) setScopeMode(c as CdsScopeMode); }} allowClear={false} /></div>
          <label className="text-csm-sm"><span className="mb-1 block text-csm-ink-soft">{t('cds.ds.field.order', 'Default order (e.g. "name asc")')}</span>
            <input value={defaultOrder} onChange={e => setDefaultOrder(e.target.value)} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm" /></label>
          <label className="flex items-center gap-2 text-csm-sm"><input type="checkbox" checked={publicAnon} onChange={e => setPublicAnon(e.target.checked)} className="h-4 w-4 accent-primary" />{t('cds.ds.field.public_anon', 'Public (anon-readable)')}</label>
          <label className="flex items-center gap-2 text-csm-sm"><input type="checkbox" checked={canWrite} onChange={e => setCanWrite(e.target.checked)} className="h-4 w-4 accent-primary" />{t('cds.ds.field.can_write', 'Allow inline edit')}</label>
          <div className="col-span-2">
            <button onClick={() => void loadSourceColumns()} className="h-7 rounded border border-primary bg-white px-3 text-csm-sm text-primary hover:bg-csm-surface-alt">{t('cds.ds.load_columns', 'Load columns from source →')}</button>
          </div>
        </div>
      )}

      {tab === 'columns' && (
        <div className="p-1">
          {cols.length === 0 ? <div className="py-6 text-center text-csm-sm text-csm-ink-soft">{t('cds.ds.no_columns', 'Load columns from the Source tab first.')}</div> : (
            <table className="min-w-full text-csm-sm">
              <thead className="text-csm-xs uppercase tracking-wider text-csm-ink-soft"><tr>
                <th className="px-2 py-1 text-left">{t('cds.ds.expose', 'Expose')}</th><th className="px-2 py-1 text-left">{t('cds.ds.col.name', 'Column')}</th>
                <th className="px-2 py-1 text-left">{t('cds.ds.type', 'Type')}</th><th className="px-2 py-1 text-left">{t('cds.ds.format', 'Format')}</th>
                <th className="px-2 py-1 text-left">{t('cds.ds.filter', 'Filter')}</th><th className="px-2 py-1 text-left">{t('cds.ds.sort', 'Sort')}</th>
              </tr></thead>
              <tbody>
                {cols.map((c, i) => (
                  <tr key={c.column_name} className="border-t border-csm-border-soft">
                    <td className="px-2 py-1"><input type="checkbox" checked={c.include} onChange={e => setCol(i, { include: e.target.checked })} className="h-4 w-4 accent-primary" /></td>
                    <td className="px-2 py-1 font-mono">{c.column_name}</td>
                    <td className="px-2 py-1 text-csm-ink-soft">{c.data_type}</td>
                    <td className="px-2 py-1"><input value={c.format_hint} onChange={e => setCol(i, { format_hint: e.target.value })} placeholder={t('cds.ds.format_ph', 'currency…')} className="h-6 w-24 rounded border border-csm-border px-1 text-csm-xs" /></td>
                    <td className="px-2 py-1"><input type="checkbox" checked={c.is_filterable} onChange={e => setCol(i, { is_filterable: e.target.checked })} className="h-4 w-4 accent-gray-600" /></td>
                    <td className="px-2 py-1"><input type="checkbox" checked={c.is_sortable} onChange={e => setCol(i, { is_sortable: e.target.checked })} className="h-4 w-4 accent-gray-600" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'params' && (
        <div className="space-y-2 p-1">
          <button onClick={() => setParams(p => [...p, { param_code: '', bind_column: includedCols[0]?.column_name ?? '', operator: 'eq', source: 'ELEMENT_INPUT', is_required: false }])}
                  className="h-7 rounded border border-primary bg-white px-3 text-csm-sm text-primary hover:bg-csm-surface-alt">{t('cds.ds.add_param', '+ Filter param')}</button>
          {params.map((p, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2 rounded border border-csm-border-soft p-2">
              <input value={p.param_code} onChange={e => setParams(ps => ps.map((x, j) => j === i ? { ...x, param_code: e.target.value } : x))} placeholder={t('cds.ds.param_code', 'param code')} className="h-7 w-32 rounded border border-csm-border px-2 font-mono text-csm-xs" />
              <select value={p.bind_column} onChange={e => setParams(ps => ps.map((x, j) => j === i ? { ...x, bind_column: e.target.value } : x))} className="h-7 rounded border border-csm-border px-1 text-csm-xs">
                {includedCols.map(c => <option key={c.column_name} value={c.column_name}>{c.column_name}</option>)}
              </select>
              <select value={p.operator} onChange={e => setParams(ps => ps.map((x, j) => j === i ? { ...x, operator: e.target.value as CdsOperator } : x))} className="h-7 rounded border border-csm-border px-1 text-csm-xs">
                {OPERATORS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
              <select value={p.source} onChange={e => setParams(ps => ps.map((x, j) => j === i ? { ...x, source: e.target.value as CdsParamSource } : x))} className="h-7 rounded border border-csm-border px-1 text-csm-xs">
                {PARAM_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <label className="flex items-center gap-1 text-csm-xs"><input type="checkbox" checked={p.is_required} onChange={e => setParams(ps => ps.map((x, j) => j === i ? { ...x, is_required: e.target.checked } : x))} className="h-3.5 w-3.5 accent-primary" />{t('cds.ds.required', 'required')}</label>
              <button onClick={() => setParams(ps => ps.filter((_, j) => j !== i))} className="ml-auto text-csm-xs text-csm-danger hover:underline">{t('common.remove', 'Remove')}</button>
            </div>
          ))}
        </div>
      )}

      {tab === 'preview' && (
        <div className="p-1">
          {!preview ? <div className="py-6 text-center text-csm-sm text-csm-ink-soft">{t('cds.ds.preview_hint', 'Click “Save & preview” to run the dataset.')}</div> : (
            <div className="overflow-x-auto">
              <div className="mb-1 text-csm-xs text-csm-ink-soft">{t('cds.ds.preview_count', '{{n}} rows', { n: preview.rows.length })}</div>
              <table className="min-w-full text-csm-sm">
                <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft"><tr>{preview.columns.map(c => <th key={c.key} className="px-2 py-1 text-left">{c.key}</th>)}</tr></thead>
                <tbody>{preview.rows.slice(0, 50).map((r, i) => <tr key={i} className="border-t border-csm-border-soft">{preview.columns.map(c => <td key={c.key} className="px-2 py-1">{r[c.key] == null ? '—' : String(r[c.key])}</td>)}</tr>)}</tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
